import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { SortOrder, SummaryReportView } from '@app/services/summary.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { RestUserModel } from '@shared/interfaces/user.interface';

export interface SummaryReportSettingsModelBase {
  name: string;
  description?: string;
  summaryType: SummaryType;
  dataSource: SummaryDataSource;
  reportView: SummaryReportView;
  sortBy: SortByInfoModel[];
  showBy?: ShowByFieldInfoModel[];
  filterBy?: FilterByFieldInfoModel[];
  dataSourceModifier?: string;
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  rolesCanView?: number[];
  rolesCanEdit?: number[];
  defaultForRoles?: number[];
}

export interface SummaryReportSettingsModel extends SummaryReportSettingsModelBase {
  id?: number;
  dateCreated?: string;
  createdBy?: RestUserModel;
}

export interface ReportQueryParams {
  portfolioId?: number;
  propertyIds?: number[];
  collectionIds?: number[];
  summaryReportSettings: SummaryReportSettingsModel;
}

export interface BoardSummaryReportQueryParams extends ReportQueryParams {
  queryType: number;
  dateFieldsLookupMode: number;
  includeUnscheduled: boolean;
}

export interface TicketsSummaryReportQueryParams extends ReportQueryParams {
  excludeBoardTickets: boolean;
  periodDateSearchFields: number;
}

export interface InspectionSummaryReportQueryParams extends ReportQueryParams {
  isResident: boolean;
}

export enum SummaryReportStatus {
  NotStarted,
  InProgress,
  Completed,
  Error,
}

export interface SummaryReportOffline {
  id: number;
  reportType: number;
  payload: string;
  status: SummaryReportStatus;
  createdBy: number;
  createdAt: Date;
  lockedTill: Date;
  generatedOn?: Date;
  statusDetails?: string;
  fileUrl?: string;
}

export enum SummaryType {
  Turns,
  BB,
  Inspection,
  Service,
}

export enum SummaryDataSource {
  BB_All,
  BB_ById,
  BB_ByType,
  Inspection_Standard_All,
  Inspection_Recurring,
  Service_All,
  Service_OpenTickets,
  Service_CompletedByDate,
  Service_ServiceTickets,
  Turns_All,
  Turns_ById,
  Turns_ByType,
  Inspection_Resident_All,
}

export interface SortByInfoModel {
  columnName?: string;
  isDescending?: boolean;
}

export interface ShowByFieldInfoModel {
  label?: string;
  customFieldId?: number;
  fieldType?: number;
  isDescending?: boolean;
  sortOrder?: SortOrder;
}

export interface FilterByFieldInfoModel {
  customFieldId?: number;
  fieldType?: number;
  customFieldModifier?: number;
  values?: string;
  labels?: string;
}

export interface SummarySelectFilterConfig {
  id: number;
  label: string;
}

export enum PeriodType {
  Last7Days = 0,
  Last30Days = 1,
  Last60Days = 2,
  Last3Months = 3,
  Last6Month = 4,
  LastYear = 5,
  Custom = 6,
  ThisWeek = 7,
  Next30Days = 8,
  Next60Days = 9,
  Next6Month = 10,
  ThisYear = 11,
  Next2Weeks = 12,
  ThisMonth = 13,
  ThisQuarter = 14,
  AllTime = 15,
}

export const FilterPeriodConfig: SummarySelectFilterConfig[] = [
  {
    id: PeriodType.Last7Days,
    label: 'last 7 days',
  },
  {
    id: PeriodType.Last30Days,
    label: 'last 30 days',
  },
  {
    id: PeriodType.Last60Days,
    label: 'last 60 days',
  },
  {
    id: PeriodType.Last3Months,
    label: 'last 3 months',
  },
  {
    id: PeriodType.Last6Month,
    label: 'last 6 months',
  },
  {
    id: PeriodType.LastYear,
    label: 'last year',
  },
  {
    id: PeriodType.Custom,
    label: 'custom',
  },
  {
    id: PeriodType.ThisWeek,
    label: 'this week',
  },
  {
    id: PeriodType.Next30Days,
    label: 'next 30 days',
  },
  {
    id: PeriodType.Next60Days,
    label: 'next 60 days',
  },
  {
    id: PeriodType.Next6Month,
    label: 'next 6 months',
  },
  {
    id: PeriodType.ThisYear,
    label: 'this year',
  },
  {
    id: PeriodType.Next2Weeks,
    label: 'next 2 weeks',
  },
  {
    id: PeriodType.ThisMonth,
    label: 'this month',
  },
  {
    id: PeriodType.ThisQuarter,
    label: 'this quarter',
  },
  {
    id: PeriodType.AllTime,
    label: 'all time',
  },
];

@Injectable({
  providedIn: 'root',
})
export class SummaryReportSettingsService extends PuSubscribable {
  private readonly route = 'SummaryReportSettings';

  constructor(private restapiService: RestapiService) {
    super();
  }

  getAll(): Observable<SummaryReportSettingsModel[]> {
    return this.restapiService.getData<SummaryReportSettingsModel[]>(this.route);
  }

  add(setting: SummaryReportSettingsModelBase): Observable<SummaryReportSettingsModel> {
    return this.restapiService.post<SummaryReportSettingsModel>(this.route, setting);
  }

  update(id: number, setting: SummaryReportSettingsModel): Observable<SummaryReportSettingsModel> {
    return this.restapiService.update<SummaryReportSettingsModel>(`${this.route}/${id}`, setting);
  }

  delete(id: number): Observable<void> {
    return this.restapiService.delete<void>(`${this.route}/${id}`);
  }

  getReportStatus(id: number): Observable<SummaryReportOffline> {
    return this.restapiService.getData<SummaryReportOffline>(`exportReportRequests/${id}`);
  }
}
